import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, TIMEZONES, LOCALES, setPreferences, getLocale, getCurrency, getTimezone } from "@/lib/prefs";
import { detectCurrencyFromLocation } from "@/lib/locale";
import { Settings2, Loader2, MapPin, Search, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import RankManager from "@/components/RankManager";

export function SettingsTrigger({ className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className={`w-full justify-start gap-2 ${className || ""}`}
        onClick={() => setOpen(true)}
        data-testid="settings-btn"
      >
        <Settings2 className="w-4 h-4" /> Preferences
      </Button>
      <SettingsDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export default function SettingsDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    currency: user?.preferences?.currency || getCurrency(),
    locale: user?.preferences?.locale || getLocale(),
    timezone: user?.preferences?.timezone || getTimezone(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        currency: user.preferences?.currency || getCurrency(),
        locale: user.preferences?.locale || getLocale(),
        timezone: user.preferences?.timezone || getTimezone(),
      });
    }
  }, [open, user]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/preferences", form);
      setPreferences(data.preferences || {});
      await refresh();
      toast.success("Preferences saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Preferences</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Currency, locale and timezone. Applied everywhere in the app.</p>
        <div className="space-y-4 pt-2">
          <div>
            <div className="flex items-center justify-between">
              <Label>Currency</Label>
              <button
                type="button"
                className="text-[0.65rem] inline-flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                onClick={() => {
                  const detected = detectCurrencyFromLocation();
                  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "your device"; } })();
                  setForm((f) => ({ ...f, currency: detected, timezone: f.timezone || tz }));
                  toast.success(`Detected ${detected} from ${tz}`);
                }}
                data-testid="settings-currency-detect"
              >
                <MapPin className="w-3 h-3" /> Detect from location
              </button>
            </div>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Locale (date & number format)</Label>
            <Select value={form.locale} onValueChange={(v) => setForm({ ...form, locale: v })}>
              <SelectTrigger data-testid="settings-locale"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {LOCALES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Timezone</Label>
            <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
              <SelectTrigger data-testid="settings-timezone"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <IndexNowSection />
        <RankManagerSection />
        <ReplayTourSection onClose={() => onOpenChange(false)} />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="settings-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IndexNowSection() {
  const [pinging, setPinging] = useState(false);
  const [result, setResult] = useState(null);

  const ping = async () => {
    setPinging(true);
    setResult(null);
    try {
      const { data } = await api.post("/seo/index-now", {});
      setResult(data);
      const ok = Object.values(data.engines || {}).filter((e) => e.ok).length;
      const total = Object.keys(data.engines || {}).length;
      toast.success(`Indexed on ${ok}/${total} engines`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPinging(false);
    }
  };

  const engineLabels = {
    indexnow: "IndexNow (Bing / DuckDuckGo / Yahoo)",
    bing: "Bing (direct)",
    yandex: "Yandex",
    google_sitemap_ping: "Google sitemap ping",
  };

  return (
    <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1"><Search className="w-3 h-3" /> Search visibility</div>
          <p className="text-xs text-muted-foreground mt-1">
            Notify Bing, Yandex, DuckDuckGo and ping Google's sitemap now: instead of waiting for the next crawl.
          </p>
        </div>
        <Button
          size="sm"
          onClick={ping}
          disabled={pinging}
          className="gap-2 shrink-0"
          data-testid="settings-index-now"
        >
          {pinging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          Index now
        </Button>
      </div>
      {result && (
        <div className="rounded-lg border border-white/10 p-3 space-y-1.5 text-xs" data-testid="index-now-result">
          {Object.entries(result.engines || {}).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-white/70">{engineLabels[key] || key}</span>
              <span className={`inline-flex items-center gap-1 tabular ${val.ok ? "text-[hsl(var(--success))]" : "text-destructive/80"}`}>
                {val.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {val.status ?? "err"}
              </span>
            </div>
          ))}
          <div className="text-[10px] text-muted-foreground pt-1 border-t border-white/5 mt-1">
            Submitted {result.submitted?.length || 0} URL(s). Google requires manual Search Console setup for full indexing.
          </div>
        </div>
      )}
    </div>
  );
}

function ReplayTourSection({ onClose }) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);

  const replay = async () => {
    setStarting(true);
    try {
      // Pick the user's first recipe as the tour target. Falls back to the
      // Recipes list with a friendly nudge if there are none yet.
      const { data } = await api.get("/recipes");
      const first = Array.isArray(data) && data.length ? data[0] : null;
      if (!first) {
        toast.info("Create a recipe first. Then replay the tour on it.");
        onClose?.();
        navigate("/recipes");
        return;
      }
      onClose?.();
      navigate(`/recipes/${first.id}?tour=1`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-white/10">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1"><Sparkles className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Onboarding tour</div>
          <p className="text-xs text-muted-foreground mt-1">
            Replay the 30-second walkthrough to onboard a new chef to the costing engine.
          </p>
        </div>
        <Button
          size="sm"
          onClick={replay}
          disabled={starting}
          className="gap-2 shrink-0"
          data-testid="settings-replay-tour"
        >
          {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Replay tour
        </Button>
      </div>
    </div>
  );
}

// RankManagerSection: appears inside the Preferences dialog. Notifies the
// global roster via a window event when the operator saves a new
// hierarchy so open Roster grids re-sort without a page refresh.
function RankManagerSection() {
  const handleSaved = () => {
    try { window.dispatchEvent(new Event("cs:rank-hierarchy-changed")); } catch {}
  };
  return (
    <div className="mt-6 pt-6 border-t border-white/10">
      <RankManager onSaved={handleSaved} />
    </div>
  );
}

